import { useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import { Table, Icon, TableHeaderSort } from '@/ui'
import { DateTime } from 'luxon'

import { sortReports } from '@/selectors/reportSelectors'
import { BookkeepingReport } from '@/reducers/finances/bookkeepingReportsReducer'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { faCircleCheck, faCircle } from '@fortawesome/pro-regular-svg-icons'
const BookkeepingReportTableRow = ({
  report,
}: {
  report: BookkeepingReport
}) => (
  <Table.Row
    className={[
      report.completedAt
        ? 'bg-green-50'
        : report.dueDate && DateTime.fromISO(report.dueDate) < DateTime.now()
          ? 'bg-red-50'
          : '',
    ].join(' ')}
  >
    <Table.Cell>{report.id}</Table.Cell>
    <Table.Cell>
      <b>
        {report.reports?.financialProfile?.firstName}{' '}
        {report.reports?.financialProfile?.lastName}
      </b>
    </Table.Cell>
    <Table.Cell>
      {DateTime.fromISO(report.date).toFormat(DATE_FORMATS_LUXON.MONTH_YEAR)}
    </Table.Cell>
    <Table.Cell>
      {report.dueDate
        ? DateTime.fromISO(report.dueDate).toFormat(
            DATE_FORMATS_LUXON.DISPLAY_SHORT
          )
        : 'None'}
    </Table.Cell>
    <Table.Cell textAlign="center">
      <Icon
        icon={
          report.statuses[0]?.status === 'categorized'
            ? faCircleCheck
            : faCircle
        }
        size="lg"
      />
    </Table.Cell>
    <Table.Cell textAlign="center">
      <Icon
        icon={report.documentsCount === '0' ? faCircle : faCircleCheck}
        size="lg"
      />
    </Table.Cell>
    <Table.Cell textAlign="center">
      <Icon icon={report.reconciledAt ? faCircleCheck : faCircle} size="lg" />
    </Table.Cell>
    <Table.Cell textAlign="center">
      <Icon icon={report.completedAt ? faCircleCheck : faCircle} size="lg" />
    </Table.Cell>
    <Table.Cell>
      <Link
        to={`/admin/finances/records/${report.reports?.userId}/monthly-books/${report.id}`}
      >
        View Books
      </Link>
    </Table.Cell>
  </Table.Row>
)

const BookkeepingReportTable = ({
  reports,
}: {
  reports: { [p: string]: BookkeepingReport }
}) => {
  const [sortColumn, setSortColumn] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<
    'descending' | 'ascending'
  >()
  const [sortedReports, setSortedReports] = useState(
    Object.values(reports || {})
  )
  const resortReports = useCallback(
    (sortColumn: string) => {
      const newSortDirection =
        sortDirection === 'ascending' ? 'descending' : 'ascending'
      setSortDirection(newSortDirection)
      setSortColumn(sortColumn)
      setSortedReports(sortReports(sortedReports, sortColumn, newSortDirection))
    },
    [sortDirection, sortedReports]
  )

  return (
    <Table compact="very">
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell
            sorted={
              sortColumn === 'id'
                ? sortDirection === 'ascending'
                  ? TableHeaderSort.ASCENDING
                  : TableHeaderSort.DESCENDING
                : undefined
            }
            onClick={() => resortReports('id')}
            width={1}
          >
            ID
          </Table.HeaderCell>
          <Table.HeaderCell
            sorted={
              sortColumn === 'reports.financialProfile.firstName'
                ? sortDirection === 'ascending'
                  ? TableHeaderSort.ASCENDING
                  : TableHeaderSort.DESCENDING
                : undefined
            }
            onClick={() => resortReports('reports.financialProfile.firstName')}
            width={2}
          >
            Name
          </Table.HeaderCell>
          <Table.HeaderCell
            sorted={
              sortColumn === 'date'
                ? sortDirection === 'ascending'
                  ? TableHeaderSort.ASCENDING
                  : TableHeaderSort.DESCENDING
                : undefined
            }
            onClick={() => resortReports('date')}
            width={1}
          >
            Bookkeeping Month
          </Table.HeaderCell>
          <Table.HeaderCell
            sorted={
              sortColumn === 'dueDate'
                ? sortDirection === 'ascending'
                  ? TableHeaderSort.ASCENDING
                  : TableHeaderSort.DESCENDING
                : undefined
            }
            onClick={() => resortReports('dueDate')}
            width={2}
          >
            Due Date
          </Table.HeaderCell>
          <Table.HeaderCell
            sorted={
              sortColumn === 'status'
                ? sortDirection === 'ascending'
                  ? TableHeaderSort.ASCENDING
                  : TableHeaderSort.DESCENDING
                : undefined
            }
            onClick={() => resortReports('status')}
            width={2}
          >
            Bookkeeping Complete
          </Table.HeaderCell>
          <Table.HeaderCell
            sorted={
              sortColumn === 'documentsCount'
                ? sortDirection === 'ascending'
                  ? TableHeaderSort.ASCENDING
                  : TableHeaderSort.DESCENDING
                : undefined
            }
            onClick={() => resortReports('documentsCount')}
            width={2}
          >
            Documents
          </Table.HeaderCell>
          <Table.HeaderCell
            sorted={
              sortColumn === 'reconciledAt'
                ? sortDirection === 'ascending'
                  ? TableHeaderSort.ASCENDING
                  : TableHeaderSort.DESCENDING
                : undefined
            }
            onClick={() => resortReports('reconciledAt')}
            width={2}
          >
            Reconciled
          </Table.HeaderCell>
          <Table.HeaderCell
            sorted={
              sortColumn === 'completedAt'
                ? sortDirection === 'ascending'
                  ? TableHeaderSort.ASCENDING
                  : TableHeaderSort.DESCENDING
                : undefined
            }
            onClick={() => resortReports('completedAt')}
            width={2}
          >
            Done
          </Table.HeaderCell>
          <Table.HeaderCell width={2} className="noSort">
            Actions
          </Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {Object.values(sortedReports).map((report) => (
          <BookkeepingReportTableRow report={report} key={report.id} />
        ))}
      </Table.Body>
    </Table>
  )
}
export default BookkeepingReportTable
